/**
 * JOCategorySeoInjector — Rewrites <title>, <meta name="description">, and
 * Open Graph tags dynamically based on the ?category=X query param on /shop.
 *
 * When a category is selected, we pull its SEO metadata from
 * /api/product-categories and update the document head. Also injects a small
 * intro_html block above the product grid.
 *
 * NON-CORE — additive DOM injector.
 */
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTRO_ID = "jo-category-intro-block";

function setMeta(name, value, isProperty = false) {
  if (!value) return;
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
}

function removeIntro() {
  const el = document.getElementById(INTRO_ID);
  if (el) el.remove();
}

function ensureIntroBlock(cat) {
  removeIntro();
  if (!cat?.intro_html) return;
  // Find the shop page header (the navy "Notre sélection" section)
  const heroSections = document.querySelectorAll("section");
  let target = null;
  for (const s of heroSections) {
    if (/Notre sélection|Boutique/.test(s.textContent || "")) {
      target = s;
      break;
    }
  }
  if (!target) return;
  const block = document.createElement("section");
  block.id = INTRO_ID;
  block.setAttribute("data-testid", "jo-category-intro");
  block.className = "py-8 bg-white border-b border-neutral-200";
  block.innerHTML = `
    <div class="max-w-4xl mx-auto px-4 md:px-8 prose prose-neutral">
      <div class="text-xs uppercase tracking-[0.2em] font-bold text-[color:var(--brand-navy,#0e2a4d)] mb-2">
        ${cat.name || ""}
      </div>
      ${cat.intro_html}
    </div>
  `;
  target.parentElement?.insertBefore(block, target.nextSibling);
}

export default function JOCategorySeoInjector() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);

  // Fetch categories once — small collection
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/product-categories`).then((r) => {
      if (alive) setCategories(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    // Only act on /shop routes
    const isShop = location.pathname === "/shop" || location.pathname.startsWith("/shop/");
    if (!isShop) {
      removeIntro();
      return;
    }
    const catSlug = searchParams.get("category");
    const cat = catSlug ? categories.find((c) => c.slug === catSlug) : null;

    if (cat) {
      if (cat.seo_title) document.title = cat.seo_title;
      setMeta("description", cat.seo_description || cat.description || "");
      setMeta("og:title", cat.og_title || cat.seo_title || cat.name, true);
      setMeta("og:description", cat.og_description || cat.seo_description || "", true);
      setMeta("og:type", "website", true);
      const ogImg = cat.og_image || cat.image_url;
      if (ogImg) {
        const abs = ogImg.startsWith("http")
          ? ogImg
          : `${window.location.origin}${ogImg}`;
        setMeta("og:image", abs, true);
        setMeta("twitter:image", abs);
      }
      setMeta("twitter:card", "summary_large_image");
      setMeta("twitter:title", cat.og_title || cat.name);
      setMeta("twitter:description", cat.og_description || cat.seo_description || "");
      // Retry to insert the intro block (shop page mounts async)
      let tries = 0;
      const iv = setInterval(() => {
        tries++;
        ensureIntroBlock(cat);
        if (document.getElementById(INTRO_ID) || tries > 20) clearInterval(iv);
      }, 250);
      return () => clearInterval(iv);
    } else {
      // Global shop page (no category) — remove per-category intro
      removeIntro();
    }
  }, [location.pathname, searchParams, categories]);

  return null;
}
